// Exports: calculatePriorityFromDueDate, calculateStatusAndDays, normalizeGroup, normalizeTask, normalizeSubtask
import {
  deriveSubtaskStatus,
  formatDateDDMMYY,
  priorityText,
  todayISO,
} from "utils/tasks/taskHelpers";

export const calculatePriorityFromDueDate = (dueDate) => {
  if (!dueDate) return 2;

  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 3;
  if (diffDays <= 3) return 3;
  if (diffDays <= 7) return 2;
  return 1;
};

export const calculateStatusAndDays = (dueDate) => {
  if (!dueDate) return { statusType: "onTime", statusDays: 0 };

  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { statusType: "overdue", statusDays: Math.abs(diffDays) };
  }

  return { statusType: "onTime", statusDays: diffDays };
};

const toPercent = (count, total) => {
  if (!total) return 0;
  return Number(((count / total) * 100).toFixed(1));
};

export const normalizeTask = (rawTask, groupId, expandedTasks = {}) => {
  const id = rawTask?.task_id ?? rawTask?.id;

  return {
    id,
    task_group: groupId,
    torder_index:
      rawTask?.torder_index ??
      rawTask?.task_order ??
      rawTask?.taskOrder ??
      null,
    task_order: rawTask?.task_order ?? rawTask?.taskOrder ?? null,
    taskOrder: rawTask?.task_order ?? rawTask?.taskOrder ?? null,
    name: rawTask?.task_name ?? rawTask?.name ?? "(no-title)",
    status: rawTask?.task_status ?? rawTask?.status ?? "Ongoing",
    priority: priorityText(rawTask?.priority),
    priorityId: rawTask?.priority ?? 2,
    dueDate: rawTask?.end_date || rawTask?.dueDate || null,
    task_details: rawTask?.task_details || "–",
    assignee: rawTask?.assignee || "Unassigned",
    start_date: rawTask?.start_date || todayISO(),
    end_date: rawTask?.end_date || todayISO(),
    statusType: "overdue",
    statusDays: null,
    created_by: rawTask?.created_by || "Unknown",
    createdAt: formatDateDDMMYY(rawTask?.created_on),
    members: rawTask?.allocated_members || rawTask?.members || [],
    task_members: rawTask?.task_members || [],
    expanded: Boolean(expandedTasks[id] ?? rawTask?.expanded),
    subtasks_summary: rawTask?.subtasks_summary || {
      total_subtasks: 0,
      completed_subtasks: 0,
    },
    subtasks: Array.isArray(rawTask?.subtasks) ? rawTask.subtasks : [],
    subtasksFetched: Array.isArray(rawTask?.subtasks) && rawTask.subtasks.length > 0,
    estimated_amount: rawTask?.estimated_amount || 0,
    is_completed: rawTask?.is_completed || false,
    is_linked: Boolean(rawTask?.is_linked),
  };
};

export const normalizeSubtask = (rawSubtask, groupId, task = {}) => {
  const id = rawSubtask?.subtask_id ?? rawSubtask?.id;

  return {
    id,
    task_id: rawSubtask?.task_id ?? task?.id,
    subtask_order_index:
      rawSubtask?.subtask_order_index ?? rawSubtask?.subtaskOrder ?? null,
    name: rawSubtask?.subtask_name ?? rawSubtask?.name ?? "(no-title)",
    status:
      rawSubtask?.status || deriveSubtaskStatus(rawSubtask, task?.status),
    subtask_details: rawSubtask?.subtask_details || "–",
    is_completed: Boolean(rawSubtask?.is_completed),
    dueDate: rawSubtask?.end_date || rawSubtask?.dueDate || "",
    start_date: rawSubtask?.start_date || todayISO(),
    end_date: rawSubtask?.end_date || todayISO(),
    completed_on: rawSubtask?.completed_on || null,
    completed_by: rawSubtask?.completed_by || null,
    priority: priorityText(rawSubtask?.priority),
    priorityId: rawSubtask?.priority ?? 2,
    assignee: rawSubtask?.assign_to || rawSubtask?.assignee || [],
    statusType: "overdue",
    statusDays: 5,
    created_by: rawSubtask?.created_by || "",
    task_group: groupId,
  };
};

export const normalizeGroup = (rawGroup) => {
  const id = rawGroup?.task_group_id ?? rawGroup?.id ?? rawGroup?.pk;
  const name =
    rawGroup?.task_group_name ?? rawGroup?.group_name ?? rawGroup?.name ?? "";

  if (!id || !name.trim()) return null;

  const rawTasks = Array.isArray(rawGroup?.tasks) ? rawGroup.tasks : [];
  const taskSummary = rawGroup?.task_group_summary || {};
  const totalTasks = taskSummary?.total_tasks || rawTasks.length || 0;
  const priorityValue = rawGroup?.priority ?? calculatePriorityFromDueDate(rawGroup?.end_date);
  const { statusType, statusDays } = calculateStatusAndDays(rawGroup?.end_date);

  return {
    id,
    name,
    author: rawGroup?.created_by || "Unknown",
    assignee: rawGroup?.assignee || "Unassigned",
    startDate: rawGroup?.start_date || todayISO(),
    endDate: rawGroup?.end_date || todayISO(),
    dueDate: rawGroup?.end_date || "",
    color: "#9c27b0",
    statusPercentages: {
      completed: toPercent(taskSummary?.completed_tasks || 0, totalTasks),
      inProgress: toPercent(taskSummary?.ongoing_tasks || 0, totalTasks),
      notStarted: toPercent(taskSummary?.not_started_tasks || 0, totalTasks),
      overdue: toPercent(taskSummary?.overdue_tasks || 0, totalTasks),
    },
    priorityPercentages: {
      high: toPercent(taskSummary?.high_priority || 0, totalTasks),
      medium: toPercent(taskSummary?.medium_priority || 0, totalTasks),
      low: toPercent(taskSummary?.low_priority || 0, totalTasks),
    },
    subtasks_summary: {
      total_subtasks: taskSummary?.total_subtasks || 0,
      completed_subtasks: taskSummary?.completed_subtasks || 0,
      progress_percentage: (taskSummary?.progress || 0).toFixed(1),
    },
    statusLabel: rawGroup?.status || "Not started",
    priority: priorityText(priorityValue),
    priorityValue,
    statusDays,
    statusType,
    allocated_members: rawGroup?.allocated_members || [],
    expanded: Boolean(rawGroup?.expanded),
    tasks: rawTasks.map((task) => normalizeTask(task, id)),
    tasksFetched: rawTasks.length > 0 || Boolean(rawGroup?.tasksFetched),
    approvers: rawGroup?.approvers || [],
  };
};
